import { useState, useEffect, useRef } from 'react';
import supabase from '../utils/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

const useRoomAnalytics = (roomId, roomCode) => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState({
    totalSpins: 0,
    totalPlayers: 0,
    sessionStartTime: null,
    mostPopularSegment: null,
    segmentCounts: {}
  });
  
  const sessionStartRef = useRef(null);
  const analyticsIntervalRef = useRef(null);

  // Initialize session tracking
  useEffect(() => {
    if (roomId && user) {
      sessionStartRef.current = new Date();
      setAnalytics(prev => ({
        ...prev,
        sessionStartTime: sessionStartRef.current
      }));
      
      // Save analytics every 30 seconds
      analyticsIntervalRef.current = setInterval(() => {
        saveAnalytics();
      }, 30000);
    }

    return () => {
      if (analyticsIntervalRef.current) {
        clearInterval(analyticsIntervalRef.current);
      }
      // Save final analytics when component unmounts
      if (roomId && user) {
        saveAnalytics();
      }
    };
  }, [roomId, user]);

  // Track spin result
  const trackSpin = (result, playerName) => {
    setAnalytics(prev => {
      const newSegmentCounts = { ...prev.segmentCounts };
      newSegmentCounts[result] = (newSegmentCounts[result] || 0) + 1;
      
      // Find most popular segment
      const mostPopular = Object.entries(newSegmentCounts)
        .reduce((a, b) => newSegmentCounts[a[0]] > newSegmentCounts[b[0]] ? a : b)[0];

      return {
        ...prev,
        totalSpins: prev.totalSpins + 1,
        segmentCounts: newSegmentCounts,
        mostPopularSegment: mostPopular
      };
    });
  };

  // Track player count
  const trackPlayerCount = (playerCount) => {
    setAnalytics(prev => ({
      ...prev,
      totalPlayers: Math.max(prev.totalPlayers, playerCount)
    }));
  };

  // Save analytics to database
  const saveAnalytics = async () => {
    if (!roomId || !user || !sessionStartRef.current) {
      return;
    }

    try {
      const sessionDuration = Math.floor(
        (new Date() - sessionStartRef.current) / (1000 * 60)
      ); // Duration in minutes

      const analyticsData = {
        room_id: roomId,
        user_id: user.id,
        total_spins: analytics.totalSpins,
        total_players: analytics.totalPlayers,
        session_duration_minutes: sessionDuration,
        most_popular_segment: analytics.mostPopularSegment,
        game_date: new Date().toISOString().split('T')[0] // YYYY-MM-DD format
      };

      // Check if analytics record exists for today
      const { data: existingAnalytics, error: fetchError } = await supabase
        .from('room_analytics')
        .select('id')
        .eq('room_id', roomId)
        .eq('user_id', user.id)
        .eq('game_date', analyticsData.game_date)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Error fetching existing analytics:', fetchError);
        return;
      }

      if (existingAnalytics) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('room_analytics')
          .update(analyticsData)
          .eq('id', existingAnalytics.id);

        if (updateError) {
          console.error('Error updating analytics:', updateError);
        } else {
          console.log('Analytics updated successfully');
        }
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('room_analytics')
          .insert([analyticsData]);

        if (insertError) {
          console.error('Error inserting analytics:', insertError);
        } else {
          console.log('Analytics inserted successfully');
        }
      }
    } catch (error) {
      console.error('Error saving analytics:', error);
    }
  };

  // Note: Analytics display functions removed - data is only stored in DB for admin analysis

  return {
    analytics,
    trackSpin,
    trackPlayerCount,
    saveAnalytics
  };
};

export default useRoomAnalytics; 